/**
 * Shared type definitions for control components
 *
 * These types define the common props structure used across different control components
 * to avoid redundancy and ensure consistency.
 */

import {type SliderKeyboardOperation} from '@wandb/weave/common/util/media';
import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../Component';
import {type LayoutConfigContextValue} from '../layout/contexts/LayoutConfigContext';
import {
  type ControlDefinition,
  type GridCell,
  type GridData,
  type LabelPosition,
} from '../layout/types';
import {type TilePagination} from '../tiles';
import {type StepMeta} from '../types';
/**
 * Base props shared by most control components
 */
export type BaseControlProps = {
  /** Control definition from slot configuration */
  control: ControlDefinition;
  /** Index of this control in the controls array */
  index: number;
  /** Media panel configuration */
  config: MediaConfigWithDefaults;
  /** Optional column index (for compare mode with individual controls) */
  columnIndex?: number;
  /** Optional row index (for grid mode y-axis value lookup) */
  rowIndex?: number;
  /** Optional layout configuration context value if  */
  layoutConfig?: LayoutConfigContextValue | null;
  /** Layout mode for the control:
   * - 'horizontal': Single line layout, no wrapping (slider and input side by side)
   * - 'vertical': Stacked layout (input above slider)
   * Defaults to 'horizontal'.
   */
  layout?: 'horizontal' | 'vertical';
};

/**
 * Props for controls that need step metadata and slider key
 */
export type StepMetadataProps = {
  /** Step metadata for all runs */
  stepMeta: StepMeta;
};

/**
 * Props for step-related controls (StepControl, MediaKeyControl)
 */
export type StepControlBaseProps = BaseControlProps & StepMetadataProps;

/**
 * Props for media key selector controls
 */
export type MediaKeyControlProps = BaseControlProps;

/**
 * Keyboard bindings for step slider (returned by useStepSliderKeyboardBindings hook)
 */
export type StepSliderKeyboardBindings =
  | {
      'Meta+ArrowRight': SliderKeyboardOperation;
      'Meta+ArrowLeft': SliderKeyboardOperation;
      'Control+ArrowRight': SliderKeyboardOperation;
      'Control+ArrowLeft': SliderKeyboardOperation;
    }
  | undefined;

/**
 * Active axes configuration (returned by getActiveAxes function)
 */
export type ActiveAxes = string[];

/**
 * Keyboard bindings for index slider (returned by useIndexSliderKeyboardBindings hook)
 */
export type IndexSliderKeyboardBindings =
  | {
      'Alt+ArrowRight': SliderKeyboardOperation;
      'Alt+ArrowLeft': SliderKeyboardOperation;
    }
  | undefined;

/**
 * Props for step slider controls
 */
export type StepControlProps = StepControlBaseProps & {
  /** Keyboard bindings for step slider */
  stepSliderKeyboardBindings: StepSliderKeyboardBindings;
  /** Active axes configuration */
  activeAxes: ActiveAxes;
};

/**
 * Props for index slider controls
 */
export type IndexControlProps = BaseControlProps &
  StepMetadataProps & {
    /** Keyboard bindings for index slider */
    indexSliderKeyboardBindings: IndexSliderKeyboardBindings;
    /** Active axes configuration */
    activeAxes: ActiveAxes;
  };

/**
 * Props for run selector controls
 */
export type RunControlProps = BaseControlProps & {
  /** If true, disable links in run displays */
  disableRunLinks?: boolean;
  /** If true, show color pill instead of colorizing text */
  showColorPill?: boolean;
};

/**
 * Props for axis range selection controls
 */
export type AxisRangeControlProps = {
  /** Axis to control ('xAxis' or 'yAxis') */
  axis: 'xAxis' | 'yAxis';
  /** Label for the axis */
  axisLabel: string;
  /** Grid data containing axis metadata */
  gridData: GridData;
  /** Media panel configuration */
  config: MediaConfigWithDefaults;
  /** Width available in the footer content area */
  width?: number;
  /** Optional column count for x-axis expansion */
  columnCount?: number;
  /** Config update function */
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  /** Direction to render the axis control */
  direction?: 'top' | 'bottom' | 'left' | 'right';
  /** Whether the maximum step is loading */
  isMaxStepLoading?: boolean;
};

/**
 * Props for caption control component
 */
export type CaptionControlProps = BaseControlProps & {
  /** Grid cell containing run and media information */
  cell?: GridCell;
};

/**
 * Props for pagination control component
 */
export type PaginationProps = {
  /** Tile pagination state */
  tilePagination: TilePagination;
  /** Reference to panel viewer element for scroll control */
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Config update function */
  updateConfig: MediaBrowserPanelProps['updateConfig'];
};

/**
 * Props for Title component - Pure presentational component for rendering labels and values
 *
 * Handles layout/positioning of label + value combinations:
 * - 'separate': Label on separate line above value (default)
 * - 'inline': Label inline with value (e.g., "Step: 5")
 * - 'value': Only value displayed (e.g., for row headers in grid mode)
 * - 'hidden': Renders nothing
 */
export type TitleProps = {
  /** Label text to display */
  label: string;
  /** Value to display - can be a React node for complex components */
  value: React.ReactNode;
  /** Optional className for the value element */
  valueClassName?: string;
  /** Optional style for the value element */
  valueStyle?: React.CSSProperties;
  /** Container className */
  className?: string;
  /** How to position the label relative to the value */
  labelPosition?: LabelPosition;
  /** Whether to show a colon after the label in inline mode (defaults to false) */
  showColon?: boolean;
  /** How to justify inline content: 'center' or 'start' (defaults to 'center') */
  justifyContent?: 'center' | 'start';
  /** Optional test identifier */
  'data-test'?: string;
};

/**
 * Props for Slider component - Generic slider control component
 *
 * A reusable slider component that can be used for step or index controls.
 * Handles slider UI with keyboard shortcuts support.
 */
export type SliderProps = {
  min: number;
  max: number;
  current: number;
  /** Optional array of valid values to snap to */
  values?: number[];
  label?: string;
  strideLength?: number;
  keyboardBindings?: {
    [key: string]: SliderKeyboardOperation;
  };
  onChange: (value: number, source?: SliderChangeSource) => void;
  className?: string;
  'data-test'?: string;
  /**
   * Minimum label to display on the slider. Defaults to empty string.
   */
  minLabel?: string;
  /**
   * Maximum label to display on the slider. Defaults to empty string.
   */
  maxLabel?: string;
  /**
   * Whether to hide the label. Defaults to false.
   */
  showLabel?: boolean;
  /**
   * Whether to hide number input. Defaults to false.
   */
  showInput?: boolean;
  /**
   * Whether to snap to values in the values array. Defaults to true.
   */
  snapToExistingValue?: boolean;
  /**
   * Optional tooltip content to display when hovering over the label.
   * If provided, a tooltip will be shown. Can be a string or React node (e.g. for hotkey components).
   */
  tooltipContent?: React.ReactNode;
  /**
   * Whether to disable the tooltip. Defaults to false.
   */
  tooltipDisabled?: boolean;
  /**
   * Optional tooltip content to display on a small info icon rendered next to
   * the label.
   */
  infoTooltip?: React.ReactNode;
  /** Whether to allow the slider and input to wrap to new lines when space is constrained. */
  compact?: boolean;
  /**
   * Layout mode for the slider:
   * - 'horizontal': Single line layout, no wrapping (slider and input side by side)
   * - 'vertical': Stacked layout (input above slider)
   * Defaults to 'horizontal'.
   */
  layout?: 'horizontal' | 'vertical';
  /**
   * Maximum width for the slider container. Defaults to '100%'.
   * Use '600px' to cap width outside compare mode, '100%' for full width in compare mode.
   */
  maxWidth?: string;
  /**
   * Whether to show tick marks below the slider for discrete values.
   * Only applies when snapToExistingValue is true and values array is provided.
   * Defaults to false.
   */
  showTicks?: boolean;
};

export enum SliderChangeSource {
  Slider = 'slider',
  Input = 'input',
  InputStepper = 'input_stepper',
  Keyboard = 'keyboard',
  CardDrag = 'card_drag',
}
